import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useOutsideAlerter from './hooks/useOutsideAlerter';
import useAuditErrorCategories from 'react-scale/components/audits/SpeedAudit/v2/hooks/UseAuditErrorCategories';
import { TextInput, Button, Chip } from 'design-system';
/* eslint-disable restrict-imports/restrict-imports */
import Select, { SelectOption } from 'corp/components/UI/Select';

interface SpeedAuditErrorPickerProps {
  projectId: string;
  selectedErrorCategories: string[];
  enableCreateNewError: boolean;
  toggleChoose: (errorCategory: string[], setVsFilter?: boolean) => void;
}

const stringArrayToSelectArray = (values: string[]): SelectOption[] => {
  return values.map(value => ({ label: value, value }));
};

const SpeedAuditErrorPicker: React.FC<SpeedAuditErrorPickerProps> = ({
  projectId,
  selectedErrorCategories,
  enableCreateNewError = false,
  toggleChoose,
}) => {
  const [open, setOpen] = useState(false);
  const [makingNewCategory, setMakingNewCategory] = useState(false);
  const [newCategoryEditor, setNewCategoryEditor] = useState('');
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  useOutsideAlerter(wrapperRef, () => setOpen(false));
  const { errorCategories: possibleErrorCategories, addNewCategory } =
    useAuditErrorCategories(projectId);

  const options = stringArrayToSelectArray(possibleErrorCategories);
  const selectedOptions = stringArrayToSelectArray(selectedErrorCategories);

  const errorPickerClassname = 'error-picker' + (open ? ' error-picker--open' : '');

  const addNewCategoryHandler = async () => {
    if (!newCategoryEditor) return;
    await addNewCategory(newCategoryEditor);
    setMakingNewCategory(false);
  };

  const newErrorCreator = enableCreateNewError ? (
    makingNewCategory ? (
      <div className="making-new-category">
        {' '}
        <TextInput
          required
          type="text"
          value={newCategoryEditor}
          style={{ flexGrow: 1, marginRight: '5px' }}
          onChange={(e: any) => setNewCategoryEditor(e.target.value)}
        />
        <Button onClick={addNewCategoryHandler} disabled={!newCategoryEditor} type="primary">
          Add
        </Button>
        <Button onClick={() => setMakingNewCategory(false)}>Cancel</Button>
      </div>
    ) : (
      <div className={'make-new-category'} onClick={() => setMakingNewCategory(true)}>
        <FontAwesomeIcon icon={'plus'} />
        <span style={{ marginLeft: '10px' }}>Create new error type</span>
      </div>
    )
  ) : (
    <></>
  );

  return (
    <div className={errorPickerClassname} ref={wrapperRef}>
      <style jsx>{`
        .error-picker {
          position: relative;
          display: flex;
        }
        .select-head {
          border: 1px solid var(--color-LightSkyBlue);
          padding: 7px 16px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .select-head:hover {
          background: #eee;
        }

        .error-picker.error-picker--open .select-head {
          color: var(--color-primary);
          border: 1px solid var(--color-primary);
        }

        .opts-wrapper {
          position: absolute;
          left: 0;
          bottom: calc(50px + 100%);
          display: flex;
          flex-direction: column;
          width: auto;
          min-width: 200px;
          background: white;
          border: 1px solid #eee;
          border-radius: 8px;
          box-shadow: 2px 2px 2px #ccc;
        }

        .choose-error-category {
          padding: 6px 10px;
        }

        .make-new-category {
          padding: 6px 10px;
          color: var(--primary);
          cursor: pointer;
        }

        .making-new-category {
          display: flex;
          padding: 6px 10px;
          align-items: center;
          min-width: 350px;
        }

        .choose-error-category:hover,
        .make-new-category:hover {
          background: #eee;
          cursor: pointer;
        }

        .choose-error-category.choose-error-category--on {
          background: #eee;
          border-bottom: 2px solid var(--color-primary);
        }
      `}</style>
      {open && (
        <div className="opts-wrapper">
          <div>
            <Select
              fullWidth
              placeholder="Error type"
              multi={true}
              options={options}
              value={selectedOptions}
              menuMaxHeight={80}
              onChange={(newValue: SelectOption[]) => {
                const selectedString: string[] = newValue.map(element => element.value);
                toggleChoose(selectedString, true);
              }}
            />
            {newErrorCreator}
          </div>
        </div>
      )}
      <div onClick={() => setOpen(!open)} className={'select-head'}>
        <span style={{ marginRight: '8px' }}>Error Type</span>
        <FontAwesomeIcon icon={open ? 'caret-up' : 'caret-down'} />
      </div>

      {selectedOptions.length ? (
        <div style={{ display: 'flex', marginLeft: '10px', flexWrap: 'wrap' }}>
          {selectedOptions.map((selected: SelectOption, i) => (
            <Chip
              label={selected.label}
              key={i}
              onDelete={() => toggleChoose([selected.value], false)}
            />
          ))}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default SpeedAuditErrorPicker;
